/**
 * SyncWatcher.ts
 *
 * The background sync engine — the ONLY thing that calls your API.
 * Singleton class. Initialize once at app startup via SyncWatcher.initialize().
 *
 * Wake-up triggers:
 *   1. 'idb:changed' custom DOM event  (fired after every mutation)
 *   2. window 'online' event           (network restored)
 *   3. document 'visibilitychange'     (tab focused)
 *
 * Sync loop:
 *   - Acquires Web Lock (only one tab syncs at a time)
 *   - Reads all isSync: 0 ops from syncQueue, ordered by createdAt ASC
 *   - Groups by noteId, merges multiple ops into one BlockChangesPayload
 *   - Sends to API (up to MAX_CONCURRENT requests in parallel)
 *   - On success: marks ops as isSync: 1
 *   - On failure: records error, schedules exponential backoff retry
 */

import { getUnsyncedOps, markOpsSynced, recordOpFailure, countPendingOps, markOpsDeadLetter } from "../stores/syncQueueStore";
import { markBlockSynced, markBlocksSynced, recordBlockSyncFailure, getBlock, mergeServerStampedDatabaseProperties,} from "../stores/blocksStore";
import { markSidebarOrderSynced } from "../stores/sidebarOrderStore";
import { markWorkAreaSynced, recordWorkAreaSyncFailure } from "../stores/workAreasStore";
import { markDataSourceSynced, recordDataSourceSyncFailure } from "../stores/dataSourcesStore";
import { setSyncStatus, setPendingCount, recordSyncSuccess } from "../stores/syncMetaStore";
import { refreshPageCacheTimestamp, refreshPageCacheTimestamps, recordPageEditStamp, recordLocalPageEdit } from "../eviction";
import { networkMonitor } from "./networkMonitor";
import { retryScheduler } from "./retryScheduler";
import { mergeBlockOps, groupOpsByNoteId } from "./operationMerger";
import { reconcileInverseOps } from "./opReconciler";
import { getWithAuth, postWithAuth, deleteWithAuth } from "@/lib/api-helpers";
import type { SyncQueueRecord, ReminderUpsertPayload, ReminderCancelPayload, BlockDeletePayload, PageMovePayload, DataSourceUpdatePayload, ViewUpdatePayload, ViewReorderPayload, SchemaReorderPayload, LayoutUpdatePayload, PropertyVisibilityUpdatePayload, FilterUpdatePayload, SortUpdatePayload, ColumnWidthsUpdatePayload, PropertyCreatePayload,
  GroupUpdatePayload, PropertyDeletePayload, ViewAddPayload, ViewDeletePayload, PropertySchemaUpdatePayload, PageCreatePayload, UpdatePropertyValuePayload, ChartSettingsUpdatePayload, ToggleLockPayload, BlockSettingsUpdatePayload, AdvancedFilterUpdatePayload, PageSettingsUpdatePayload, DatasourcePageSettingsUpdatePayload, DatasourceSettingsUpdatePayload, DashboardSettingsUpdatePayload } from "../db/schema";
import { getIDBStatus } from "../idbStatus";
import { clearDatasourceCreating } from "@/lib/datasourceTombstones";
import type { BlockChangesPayload } from "@/types/block";

const MAX_CONCURRENT = 3;          // max parallel API requests per sync cycle (non-CREATE pipelines)
const CREATE_CONCURRENCY = 5;      // max parallel /batch-create requests within one dependency wave
const MAX_BATCH_BLOCKS = 200;       // max blocks packed into one /batch-create request
const MAX_ATTEMPTS = 5;            // after this, op is considered permanently failed

/**
 * Orders create blocks into DOCUMENT order by following the `insertAfterBlockID`
 * chain — NOT the order they were enqueued. Ops can be enqueued in a different
 * order than the document: e.g. inserting a board also adds a trailing paragraph,
 * and the paragraph's BLOCK_CREATE is enqueued (eager tracker-create) BEFORE the
 * board's (awaited createCollectionMutation). The backend inserts `blocks[]` in
 * the given order, anchored on `blocks[0].insertAfterBlockID`, so a createdAt-
 * ordered batch persists the wrong order (paragraph before board). Ordering by the
 * chain makes `blocks[0]` the root (anchored on an already-existing block) and
 * keeps siblings in document order.
 *
 * Roots (anchor null, or pointing outside this batch) keep their original relative
 * order; each block's chain-successor follows it. A `visited` guard makes a
 * malformed/cyclic `insertAfterBlockID` fall back to original order safely.
 */
function orderCreatesByInsertAfter<
  T extends { _id: string; insertAfterBlockID: string | null },
>(blocks: T[]): T[] {
  if (blocks.length < 2) return blocks;
  const inBatch = new Set(blocks.map((b) => b._id));
  const successor = new Map<string, T>(); // predecessor id -> the block right after it
  for (const b of blocks) {
    if (b.insertAfterBlockID != null) successor.set(b.insertAfterBlockID, b);
  }
  const ordered: T[] = [];
  const visited = new Set<string>();
  for (const b of blocks) {
    const anchor = b.insertAfterBlockID;
    const isRoot = anchor == null || !inBatch.has(anchor);
    if (!isRoot) continue;
    let cur: T | undefined = b;
    while (cur && !visited.has(cur._id)) {
      visited.add(cur._id);
      ordered.push(cur);
      cur = successor.get(cur._id);
    }
  }
  // Safety: any block not reached (cycle, or two blocks sharing one anchor) keeps original order.
  for (const b of blocks) if (!visited.has(b._id)) ordered.push(b);
  return ordered;
}

/**
 * True when a failed property delete failed *because the target is already gone*.
 * That is the terminal success state for a delete, not a retryable error: a ghost
 * column left in React context, a duplicate queued op, or a retry after a
 * partially-applied delete all land here, and no number of retries can improve it.
 *
 * Detection order:
 *   1. the server's stable discriminator `error: "property.already_absent"`. The
 *      delete-property route translates the two service-level "already gone"
 *      errors into a 404 whose body carries that code next to the human message.
 *   2. the human message itself (`Error("Property not found in data source")` /
 *      `Error("Data source not found")`), plus a 404 that says "not found", as a
 *      fallback so an older/rolling-out server still settles.
 *
 * CAVEAT — leg 1 cannot fire today. `deleteWithAuth` (lib/api-helpers.ts) parses
 * the error body but keeps only `errorData.message`, returning
 * `{status, message, isError}`; the `error` discriminator is discarded before it
 * ever reaches this predicate. The check is kept because it costs nothing and
 * goes live the moment api-helpers forwards `error`, but the message/status
 * fallback is what actually matches right now — do not delete it.
 *
 * A bare 500 with a generic body is NOT matched — that could be any real server
 * fault, and swallowing it would silently drop a delete that never reached the
 * server.
 */
function isAlreadyAbsentDeleteError(res: { status?: number; message?: string; error?: string }): boolean {
  if (res?.error === "property.already_absent") {
    return true;
  }
  const message = (res?.message || "").toLowerCase();
  if (
    message.includes("property not found in data source") ||
    message.includes("data source not found")
  ) {
    return true;
  }
  // 404 from this route can only mean the datasource/property is gone (a missing
  // route reads "Cannot DELETE /api/…", which has no "not found" in it).
  return res?.status === 404 && message.includes("not found");
}

class SyncWatcher {
  private workspaceId: string | null = null;
  private isSyncing = false;
  // Set when a wake arrives while a cycle is running, so runSyncLoop re-drains
  // instead of dropping it.
  private wakeRequested = false;
  private initialized = false;
  private wakeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private wakeFirstScheduledAt: number | null = null;
  private DEBOUNCE_MS = 300;        // debounce rapid idb:changed events
  private MAX_WAIT_MS = 1000;       // …but fire at least this often under continuous activity

  // ─── Initialization ──────────────────────────────────────────

  /**
   * Initialize the watcher. Call once at app startup (e.g. in providers.tsx).
   * Safe to call multiple times — will not re-register listeners.
   */
  initialize(workspaceId: string): void {
    if (this.initialized) {
      this.workspaceId = workspaceId;
      // Re-entering a workspace via SPA navigation must kick a drain: ops
      // enqueued for it while the watcher pointed at another workspace would
      // otherwise sit pending until the next edit or tab hide/show.
      this.wake();
      return;
    }

    this.workspaceId = workspaceId;
    this.initialized = true;

    if (typeof window === "undefined") return;

    // Wake on every IDB write (debounced to avoid thrashing on rapid edits)
    window.addEventListener("idb:changed", () => this.debouncedWake());

    // Wake immediately when network comes back
    networkMonitor.onOnline(() => {
      this.wake();
    });

    // Wake when tab becomes visible again
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.wake();
      }
    });

    networkMonitor.initialize();

    // Initial wake on startup
    this.wake();
  }

  // ─── Wake ────────────────────────────────────────────────────

  /**
   * Debounced wake — avoids multiple sync loops for rapid back-to-back edits,
   * but with a MAX_WAIT cap so continuous activity (which keeps resetting the
   * 300ms timer) can't starve the sync indefinitely. Without the cap, a long
   * burst of idb:changed events would push the wake out forever and the queue
   * would grow unbounded until the user paused.
   */
  private debouncedWake(): void {
    const now = Date.now();
    if (this.wakeFirstScheduledAt === null) this.wakeFirstScheduledAt = now;
    if (this.wakeDebounceTimer) clearTimeout(this.wakeDebounceTimer);

    // Cap reached → fire now instead of resetting again.
    if (now - this.wakeFirstScheduledAt >= this.MAX_WAIT_MS) {
      this.wakeDebounceTimer = null;
      this.wakeFirstScheduledAt = null;
      this.wake();
      return;
    }

    this.wakeDebounceTimer = setTimeout(() => {
      this.wakeDebounceTimer = null;
      this.wakeFirstScheduledAt = null;
      this.wake();
    }, this.DEBOUNCE_MS);
  }

  /**
   * Start a sync drain. If a cycle is already running, the wake is coalesced via
   * `wakeRequested` so runSyncLoop runs another pass instead of dropping it.
   * `isSyncing` is claimed synchronously so two wakes can't both start a loop.
   */
  wake(): void {
    if (!networkMonitor.isOnline()) {
      setSyncStatus("offline");
      return;
    }
    if (this.isSyncing) {
      this.wakeRequested = true;      // coalesce into the in-flight drain
      return;
    }
    this.isSyncing = true;            // reserve the run before the first await
    void this.runSyncLoop();
  }

  // ─── Core Sync Loop ──────────────────────────────────────────

  /**
   * Owns the `isSyncing` lifecycle and drains the queue, re-running
   * executeSyncCycle for as long as a wake arrives mid-pass so work enqueued
   * during a cycle is processed in the same drain. Caller (wake) must have set
   * `isSyncing = true` synchronously before invoking.
   */
  private async runSyncLoop(): Promise<void> {
    try {
      if (!this.workspaceId) return;

      do {
        // Clear BEFORE the pass: any wake that lands during this pass re-sets the
        // flag, so the loop runs one more time and re-reads the queue.
        this.wakeRequested = false;

        // Web Locks API ensures only one tab runs a cycle at a time.
        if (typeof navigator !== "undefined" && "locks" in navigator) {
          await navigator.locks.request("idb-sync-lock", async () => {
            await this.executeSyncCycle();
          });
        } else {
          // Fallback for environments without Web Locks (e.g. some test envs)
          await this.executeSyncCycle();
        }
      } while (this.wakeRequested);
    } catch (err) {
      console.error("[SyncWatcher] Sync loop error:", err);
    } finally {
      this.isSyncing = false;
    }

    // Honor a wake that landed between the final check and clearing isSyncing.
    if (this.wakeRequested) this.wake();
  }

  private async executeSyncCycle(): Promise<void> {
    // A single pass over the queue. `isSyncing` and re-run/drain decisions are
    // owned by runSyncLoop (the sole caller); this method must not touch them.
    try {
      await setSyncStatus("syncing");
      // Re-check network inside the lock (might have gone offline while waiting)
      if (!networkMonitor.isOnline()) {
        await setSyncStatus("offline");
        return;
      }

      // If IDB is completely unavailable, there is nothing in the syncQueue to process.
      // Direct API calls from the mutation fallbacks have already reached the server,
      // so we can safely idle here.
      if (getIDBStatus() === "unavailable") {
        await recordSyncSuccess();
        return;
      }

      const workspaceId = this.workspaceId!;
      const allOps = await getUnsyncedOps(workspaceId);


      if (allOps.length === 0) {
        await recordSyncSuccess();
        return;
      }

      // Separate ops by type
      const blockCreateOps = allOps.filter((op) => op.operationType === "BLOCK_CREATE");
      const blockOps = allOps.filter((op) => op.operationType === "BLOCK_CHANGES");
      const blockDeleteOps = allOps.filter((op) => op.operationType === "BLOCK_DELETE");
      const pageMoveOps = allOps.filter((op) => op.operationType === "PAGE_MOVE");
      const sidebarOps = allOps.filter((op) => op.operationType === "SIDEBAR_REORDER");
      const reminderOps = allOps.filter(
        (op) => op.operationType === "REMINDER_UPSERT" || op.operationType === "REMINDER_CANCEL"
      );
      const workAreaOps = allOps.filter((op) =>
        ["WORKAREA_CREATE", "WORKAREA_UPDATE", "WORKAREA_DELETE"].includes(op.operationType)
      );
      const dataSourceOps = allOps.filter((op) => op.operationType === "DATASOURCE_UPDATE");
      const propertyCreateOps = allOps.filter((op) => op.operationType === "DATASOURCE_PROPERTY_CREATE");
      const viewUpdateOps = allOps.filter((op) => op.operationType === "VIEW_UPDATE");
      const viewReorderOps = allOps.filter((op) => op.operationType === "VIEW_REORDER");
      const layoutUpdateOps = allOps.filter((op) => op.operationType === "LAYOUT_UPDATE");
      const propertyVisibilityOps = allOps.filter((op) => op.operationType === "PROPERTY_VISIBILITY_UPDATE");
      const filterUpdateOps = allOps.filter((op) => op.operationType === "FILTER_UPDATE");
      const sortUpdateOps = allOps.filter((op) => op.operationType === "SORT_UPDATE");
      const columnWidthsOps = allOps.filter((op) => op.operationType === "COLUMN_WIDTHS_UPDATE");
      const groupUpdateOps = allOps.filter((op) => op.operationType === "GROUP_UPDATE");
      const propertyDeleteOps = allOps.filter((op) => op.operationType === "DATASOURCE_PROPERTY_DELETE");
      const viewAddOps = allOps.filter((op) => op.operationType === "VIEW_ADD");
      const viewDeleteOps = allOps.filter((op) => op.operationType === "VIEW_DELETE");
      const propertyUpdateOps = allOps.filter((op) => op.operationType === "DATASOURCE_PROPERTY_SCHEMA_UPDATE");
      const schemaReorderOps = allOps.filter((op) => op.operationType === "DATASOURCE_SCHEMA_REORDER");
      const updatePropertyValueOps = allOps.filter((op) => op.operationType === "UPDATE_PROPERTY_VALUE");
      const chartSettingsOps = allOps.filter((op) => op.operationType === "CHART_SETTINGS_UPDATE");
      const toggleLockOps = allOps.filter((op) => op.operationType === "TOGGLE_LOCK");
      const blockSettingsOps = allOps.filter((op) => op.operationType === "BLOCK_SETTINGS_UPDATE");
      const advancedFilterOps = allOps.filter((op) => op.operationType === "ADVANCED_FILTER_UPDATE");
      const pageSettingsOps = allOps.filter((op) => op.operationType === "PAGE_SETTINGS_UPDATE");
      const datasourcePageSettingsOps = allOps.filter((op) => op.operationType === "DATASOURCE_PAGE_SETTINGS_UPDATE");
      const datasourceSettingsOps = allOps.filter((op) => op.operationType === "DATASOURCE_SETTINGS_UPDATE");
      const dashboardSettingsOps = allOps.filter((op) => op.operationType === "DASHBOARD_SETTINGS_UPDATE");
      const blockTrashOps = allOps.filter((op) => op.operationType === "BLOCK_TRASH");
      const blockRestoreOps = allOps.filter((op) => op.operationType === "BLOCK_RESTORE");

      // ── Cross-type reconciliation ──────────────────────────────────
      // Buckets run in a fixed TYPE order, ignoring createdAt across types.
      // Resolve inverse pairs (create/delete, trash/restore) first so they
      // don't fight — see opReconciler.ts. Cancelled ops are marked synced and
      // dropped from the live buckets below.
      const { cancelledOpIds, cancelledCreateBlockIds } = reconcileInverseOps(
        blockCreateOps,
        blockDeleteOps,
        blockTrashOps,
        blockRestoreOps,
      );
      if (cancelledOpIds.size > 0) {
        const ids = [...cancelledOpIds];
        await markOpsSynced(ids);
        for (const id of ids) retryScheduler.cancel(id);
        // Cancelled creates will never sync — clear their block sync flag so
        // eviction isn't blocked waiting on them (one transaction).
        await markBlocksSynced([...cancelledCreateBlockIds]).catch(() => {});
        console.log(
          `[SyncWatcher] Reconciled ${ids.length} superseded op(s) (inverse create/delete + trash/restore pairs)`,
        );
      }
      const live = (ops: SyncQueueRecord[]) => ops.filter((op) => !cancelledOpIds.has(op.id));
      const liveCreateOps = live(blockCreateOps);
      const liveDeleteOps = live(blockDeleteOps);
      const liveTrashOps = live(blockTrashOps);
      const liveRestoreOps = live(blockRestoreOps);

      // BLOCK_CHANGES whose every target block had its create cancelled would
      // RESURRECT those blocks (batch-update upserts a missing _id). Drop such
      // ops. An op touching a mix of cancelled + live blocks is left alone (the
      // live blocks still need it).
      let liveBlockOps = blockOps;
      if (cancelledCreateBlockIds.size > 0) {
        const droppedBlockOpIds: string[] = [];
        liveBlockOps = blockOps.filter((op) => {
          const p = op.payload as BlockChangesPayload;
          const ids = [
            ...(p.creates ?? []).map((c) => c.blockId),
            ...(p.updates ?? []).map((u) => u.blockId),
          ];
          const fullyCancelled =
            ids.length > 0 && ids.every((id) => cancelledCreateBlockIds.has(id));
          if (fullyCancelled) droppedBlockOpIds.push(op.id);
          return !fullyCancelled;
        });
        if (droppedBlockOpIds.length > 0) await markOpsSynced(droppedBlockOpIds);
      }

      // ── Processing order (and why it matters) ──────────────────────────
      // Block lifecycle ops run in a fixed order, because a later op depends on
      // an earlier one having already reached the server:
      //
      //   create → move → changes → trash → restore → delete
      //
      //   • create first    — the page/block must exist before anything edits it.
      //   • move before changes — a reparent + edit sends the NEW parentId in the
      //     content update; the move must put the block under that parent first,
      //     or the server's { _id, parentId } match misses and the update fails.
      //   • move before delete — so "move then delete" can't run as "delete then
      //     move" (the move would hit a gone page, 404, and dead-letter).
      //   • restore before delete — so "restore then delete" isn't reversed.
      //
      // After the lifecycle come the database / view / property / settings ops.
      // Those are independent per type, so their order doesn't matter and most
      // just send the latest change per target. The one rule: VIEW_ADD runs
      // before any view-config op, so a brand-new view exists on the server
      // before we try to rename / filter / sort it.
      // ───────────────────────────────────────────────────────────────────

      // BLOCK_CREATE must run before BLOCK_CHANGES so the page exists in MongoDB
      // before any content updates land on it
      if (liveCreateOps.length > 0) {
        await this.processBlockCreateOps(liveCreateOps);
      }

      // Process page moves BEFORE block changes (and before trash/delete).
      //
      // Why before block changes: reparenting a block (dragging it into a new
      // container) is two ops — a PAGE_MOVE that moves the block, and a
      // BLOCK_CHANGES that saves its content carrying the NEW parentId. The
      // server's batch-update matches a block by { _id, parentId } and upserts.
      // If it runs while the block is still under its OLD parent on the server,
      // the match misses, the upsert collides with the existing _id, and the
      // request fails with a duplicate-key error. Running the move first puts the
      // block under its new parent, so the following batch-update matches and
      // succeeds.
      //
      // Why before trash/delete: a move only needs the block to exist (after
      // create); it must not run after a delete, or a move-then-delete queued in
      // the same cycle would execute as delete-then-move — the move would hit a
      // deleted page, 404, retry, and dead-letter.
      if (pageMoveOps.length > 0) {
        await this.processPageMoveOps(pageMoveOps, cancelledCreateBlockIds);
      }

      // Process block ops grouped by noteId (merge multiple ops per note). Runs
      // AFTER page moves so a reparented block is already under its new parent on
      // the server before its content update lands (see the batch-update note above).
      if (liveBlockOps.length > 0) {
        await this.processBlockOps(liveBlockOps);
      }

      // After block ops: the row stores a blockId, and the scheduler drops a
      // reminder whose block it cannot find.
      if (reminderOps.length > 0) {
        await this.processReminderOps(reminderOps);
      }

      // Process trash ops (soft-delete to trash) — run after creates/updates/moves.
      // Trash/restore pairs were reconciled above, so bucket order can no longer
      // reverse user intent.
      if (liveTrashOps.length > 0) {
        await this.processBlockTrashOps(liveTrashOps);
      }

      // Process restore ops — run before deletes so a restore-then-delete isn't reversed
      if (liveRestoreOps.length > 0) {
        await this.processBlockRestoreOps(liveRestoreOps);
      }

      // Process deletes — run after creates/updates/moves so ordering is safe
      if (liveDeleteOps.length > 0) {
        await this.processBlockDeleteOps(liveDeleteOps);
      }

      // Process sidebar reorder ops (take only the latest one)
      if (sidebarOps.length > 0) {
        await this.processSidebarOps(sidebarOps);
      }

      // Process work area ops in order
      if (workAreaOps.length > 0) {
        await this.processWorkAreaOps(workAreaOps);
      }

      // Process datasource update ops in order
      if (dataSourceOps.length > 0) {
        await this.processDataSourceOps(dataSourceOps);
      }

      // Process property create ops in order
      if (propertyCreateOps.length > 0) {
        await this.processPropertyCreateOps(propertyCreateOps);
      }

      // Process view ADD ops BEFORE any view-config ops. A brand-new view gets a
      // client-generated viewTypeId and can be renamed/filtered/sorted in the same
      // debounce window — those config ops reference a view the server hasn't seen
      // yet, so the view must be created first (else the settings 404/are lost).
      if (viewAddOps.length > 0) {
        await this.processViewAddOps(viewAddOps);
      }

      // Process view update ops (rename/icon)
      if (viewUpdateOps.length > 0) {
        await this.processViewUpdateOps(viewUpdateOps);
      }

      if (viewReorderOps.length > 0) {
        await this.processViewReorderOps(viewReorderOps);
      }

      // Process layout update ops (merge multiple layout changes)
      if (layoutUpdateOps.length > 0) {
        await this.processLayoutUpdateOps(layoutUpdateOps);
      }

      // Process property visibility ops (take latest)
      if (propertyVisibilityOps.length > 0) {
        await this.processPropertyVisibilityUpdateOps(propertyVisibilityOps);
      }

      // Process filter update ops (take latest)
      if (filterUpdateOps.length > 0) {
        await this.processFilterUpdateOps(filterUpdateOps);
      }

      // Process sort update ops (take latest)
      if (sortUpdateOps.length > 0) {
        await this.processSortUpdateOps(sortUpdateOps);
      }

      // Process column width ops (take latest)
      if (columnWidthsOps.length > 0) {
        await this.processColumnWidthsOps(columnWidthsOps);
      }

      // Process group update ops (take latest)
      if (groupUpdateOps.length > 0) {
        await this.processGroupUpdateOps(groupUpdateOps);
      }

      // Process property update ops in order
      if (propertyUpdateOps.length > 0) {
        await this.processPropertyUpdateOps(propertyUpdateOps);
      }

      if (schemaReorderOps.length > 0) {
        await this.processSchemaReorderOps(schemaReorderOps);
      }

      // Process property delete ops in order
      if (propertyDeleteOps.length > 0) {
        await this.processPropertyDeleteOps(propertyDeleteOps);
      }

      // Process view delete ops (sequentially)
      if (viewDeleteOps.length > 0) {
        await this.processViewDeleteOps(viewDeleteOps);
      }

      // Process specialized property value updates (latest-per-block-per-property)
      if (updatePropertyValueOps.length > 0) {
        await this.processUpdatePropertyValueOps(updatePropertyValueOps);
      }

      // Process chart settings ops (take latest per view)
      if (chartSettingsOps.length > 0) {
        await this.processChartSettingsUpdateOps(chartSettingsOps);
      }

      // Process dashboard settings ops (FIFO — each is potentially distinct due
      // to atomic createWidget/deleteWidget payloads; can't be coalesced).
      if (dashboardSettingsOps.length > 0) {
        await this.processDashboardSettingsUpdateOps(dashboardSettingsOps);
      }

      // Process toggle-lock ops (take latest per view)
      if (toggleLockOps.length > 0) {
        await this.processToggleLockOps(toggleLockOps);
      }

      // Process block-settings ops (merge patches per block)
      if (blockSettingsOps.length > 0) {
        await this.processBlockSettingsUpdateOps(blockSettingsOps);
      }

      // Process advanced filter ops (take latest per view)
      if (advancedFilterOps.length > 0) {
        await this.processAdvancedFilterUpdateOps(advancedFilterOps);
      }

      // Process page settings ops (take latest per page)
      if (pageSettingsOps.length > 0) {
        await this.processPageSettingsUpdateOps(pageSettingsOps);
      }

      // Process datasource-level page settings ops (take latest per dataSourceId+pageId)
      if (datasourcePageSettingsOps.length > 0) {
        await this.processDatasourcePageSettingsUpdateOps(datasourcePageSettingsOps);
      }

      if (datasourceSettingsOps.length > 0) {
        await this.processDatasourceSettingsUpdateOps(datasourceSettingsOps);
      }

      // Update pending count
      const remaining = await countPendingOps();
      await setPendingCount(remaining);

      if (remaining === 0) {
        await recordSyncSuccess();
      } else {
        // No re-wake here: mid-pass arrivals set wakeRequested (drain loop picks
        // them up) and failed ops are rescheduled by retryScheduler with backoff.
        await setSyncStatus("idle");
      }
    } catch (err) {
      console.error("[SyncWatcher] Cycle error:", err);
      await setSyncStatus("error");
      // Retry after a hard cycle error (debounced so a persistent error can't
      // tight-loop).
      this.debouncedWake();
    }
  }

  // ─── Block Create Ops ─────────────────────────────────────────

  /**
   * Processes BLOCK_CREATE ops by batching same-parent ops into a single
   * /api/note/block/batch-create call.
   *
   * Algorithm:
   *   1. Filter eligible ops (attempts < MAX_ATTEMPTS) and sort by createdAt ASC.
   *   2. Group by parentId. Ops carrying a `dataSourceDetail` are NOT isolated —
   *      the dataSource is an independent entity (upserted into databaseSources
   *      collection) and does not block merging the collection_view block with
   *      regular sibling blocks.
   *   3. Within each parent group, walk ops in createdAt order and split into
   *      sub-batches whenever the running block count would exceed
   *      MAX_BATCH_BLOCKS, or whenever a second dataSourceDetail would land in
   *      the same request (the request body has one top-level dataSourceDetail
   *      field, so two boards under one parent must go in separate batches).
   *   4. Order all sub-batches by their first op's createdAt — preserves
   *      nested-create dependencies (parent-before-child).
   *   5. Send sub-batches strictly sequentially: only one BLOCK_CREATE request
   *      is in flight at a time, the next never starts until the previous
   *      resolves.
   *
   * On HTTP failure: every op in the batch records the failure and is rescheduled
   * for retry. On the next cycle they re-batch together (or split, if some have
   * since hit MAX_ATTEMPTS).
   */
  private async processBlockCreateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Phase 1: tombstone any ops that already exhausted retries, then filter + sort
    const expiredIds = ops.filter((op) => op.attempts >= MAX_ATTEMPTS).map((op) => op.id);
    if (expiredIds.length > 0) await this.deadLetterOps(expiredIds);
    const eligible = ops
      .filter((op) => op.attempts < MAX_ATTEMPTS)
      .sort((a, b) => a.createdAt - b.createdAt);
    if (eligible.length === 0) return;

    // [DUP-DEBUG] These ops will POST to /api/note/block/batch-create. A large
    // batch arriving immediately after a page-load is the tail end of the
    // duplication chain.

    // Phase 2: group by parentId (no dataSourceDetail isolation)
    const groups = new Map<string, SyncQueueRecord[]>();
    for (const op of eligible) {
      const payload = op.payload as PageCreatePayload;
      const key = payload.parentId || `__noparent_${op.id}`;
      const list = groups.get(key);
      if (list) {
        list.push(op);
      } else {
        groups.set(key, [op]);
      }
    }

    // Phase 3: within each parent group, split into sub-batches by size and by
    // dataSourceDetail boundary. Order within each sub-batch is preserved
    // (groupOps is already createdAt-sorted from Phase 1).
    const subBatches: SyncQueueRecord[][] = [];
    for (const groupOps of groups.values()) {
      let current: SyncQueueRecord[] = [];
      let currentBlockCount = 0;
      let currentHasDataSource = false;

      for (const op of groupOps) {
        const payload = op.payload as PageCreatePayload;
        const opBlockCount = payload.blocks?.length ?? 0;
        const opHasDataSource =
          payload.dataSourceDetail !== undefined && payload.dataSourceDetail !== null;

        const wouldExceedSize =
          current.length > 0 && currentBlockCount + opBlockCount > MAX_BATCH_BLOCKS;
        const wouldDoubleDataSource = opHasDataSource && currentHasDataSource;

        if (wouldExceedSize || wouldDoubleDataSource) {
          subBatches.push(current);
          current = [];
          currentBlockCount = 0;
          currentHasDataSource = false;
        }

        current.push(op);
        currentBlockCount += opBlockCount;
        if (opHasDataSource) currentHasDataSource = true;
      }

      if (current.length > 0) subBatches.push(current);
    }

    // Phase 4: order sub-batches across all parent groups by earliest createdAt
    subBatches.sort((a, b) => a[0]!.createdAt - b[0]!.createdAt);

    console.log(
      `[SyncWatcher] BLOCK_CREATE batching: ${eligible.length} ops → ${subBatches.length} batches ` +
      `(saved ${eligible.length - subBatches.length} HTTP call(s))`
    );

    // ── Phase 5: send sub-batches with dependency-aware bounded concurrency ──
    // Run in waves:
    //   • a batch waits only for the batch that creates ITS parent block, so
    //     parent-before-child ordering holds (each wave fully resolves before the
    //     next starts → a child's parent is already on the server);
    //   • batches under DIFFERENT parents run concurrently (capped);
    //   • batches sharing a parentId stay serialized across waves (they both
    //     mutate that parent's blockIds server-side, so concurrent writes could
    //     race) — enforced by taking at most one batch per parentId per wave.

    // blockId (and created-datasource id) -> index of the sub-batch that creates it
    const createdBy = new Map<string, number>();
    subBatches.forEach((sb, idx) => {
      for (const op of sb) {
        const payload = op.payload as PageCreatePayload;
        for (const b of payload.blocks ?? []) {
          if (b?._id) createdBy.set(b._id, idx);
        }
        // A batch that creates a datasource (dataSourceDetail) also "creates"
        // that datasource id — so a later batch whose parentId is the datasource
        // (e.g. seeded rows) waits for it, not just for a parent *block*.
        const dsId = (payload as any).dataSourceDetail?._id;
        if (dsId) createdBy.set(String(dsId), idx);
      }
    });
    // For each batch: the batch that creates its parent (-1 = parent already on server)
    const parentDep = subBatches.map((sb) => {
      const pid = (sb[0]!.payload as PageCreatePayload).parentId;
      return pid != null && createdBy.has(pid) ? createdBy.get(pid)! : -1;
    });
    const parentKey = subBatches.map(
      (sb, idx) => (sb[0]!.payload as PageCreatePayload).parentId ?? `__none_${idx}`,
    );

    const sent = new Array<boolean>(subBatches.length).fill(false);
    let remaining = subBatches.length;
    let guard = 0;

    while (remaining > 0 && guard++ <= subBatches.length) {
      // Batches runnable this wave: parent satisfied, not yet sent, at most one
      // per distinct parentId (same-parent batches serialize across waves).
      const wave: number[] = [];
      const parentsThisWave = new Set<string>();
      for (let i = 0; i < subBatches.length; i++) {
        if (sent[i]) continue;
        const depOk = parentDep[i] === -1 || sent[parentDep[i]!];
        if (!depOk || parentsThisWave.has(parentKey[i]!)) continue;
        parentsThisWave.add(parentKey[i]!);
        wave.push(i);
      }
      if (wave.length === 0) break; // no progress possible → sequential fallback below

      for (let j = 0; j < wave.length; j += CREATE_CONCURRENCY) {
        const chunk = wave.slice(j, j + CREATE_CONCURRENCY);
        await Promise.all(
          chunk.map(async (i) => {
            await this.syncCreateGroup(subBatches[i]!);
            sent[i] = true;
          }),
        );
      }
      remaining = sent.reduce((n, d) => (d ? n : n + 1), 0);
    }

    // Fallback: anything unreachable (dependency cycle / orphaned parent) — send
    // sequentially so nothing is silently skipped.
    for (let i = 0; i < subBatches.length; i++) {
      if (!sent[i]) {
        await this.syncCreateGroup(subBatches[i]!);
        sent[i] = true;
      }
    }
  }

  /**
   * Sends a single group of same-parent BLOCK_CREATE ops as one merged
   * batch-create request. All ops in the group share parentId, parentTable,
   * workspaceId, workareaId, and dataSourceDetail (by construction in
   * processBlockCreateOps). Their `blocks[]` arrays are concatenated in
   * createdAt order to preserve insertion ordering.
   *
   * Success: marks every op + every created page + parent as synced; refreshes
   * pageCache for each created page.
   * Failure: records failure on every op in the group, schedules retry for each.
   */
  private async syncCreateGroup(groupOps: SyncQueueRecord[]): Promise<void> {
    if (groupOps.length === 0) return;

    const first = groupOps[0]!;
    const firstPayload = first.payload as PageCreatePayload;

    // Merge blocks[] from all ops, then order them by DOCUMENT position (the
    // insertAfterBlockID chain) rather than by createdAt. Enqueue order can differ
    // from document order (e.g. a board's trailing paragraph create is enqueued
    // before the board's), and the backend inserts blocks[] in the given order
    // anchored on blocks[0], so a createdAt-ordered batch would persist the wrong
    // order. See orderCreatesByInsertAfter.
    const mergedBlocks = orderCreatesByInsertAfter(
      groupOps.flatMap((op) => (op.payload as PageCreatePayload).blocks),
    );

    // The op carrying dataSourceDetail (a board / collection_view create) is NOT
    // necessarily groupOps[0]. Enqueue order varies — e.g. inserting a board also
    // adds a trailing paragraph, and that paragraph's BLOCK_CREATE can be enqueued
    // before the board's. Reading dataSourceDetail off the first op alone would
    // then silently drop it and create the board with no datasource (no columns).
    // Phase 3 guarantees at most ONE dataSourceDetail per sub-batch, so find it
    // wherever it sits. The backend upserts the dataSource by its own _id,
    // independent of block position, so it only needs to be present in the request.
    const dataSourceDetail = groupOps
      .map((op) => (op.payload as PageCreatePayload).dataSourceDetail)
      .find((d) => d !== undefined && d !== null);

    const mergedPayload: PageCreatePayload = {
      parentId: firstPayload.parentId,
      workspaceId: firstPayload.workspaceId,
      workareaId: firstPayload.workareaId,
      parentTable: firstPayload.parentTable,
      blocks: mergedBlocks,
      ...(dataSourceDetail !== undefined && dataSourceDetail !== null
        ? { dataSourceDetail }
        : {}),
    };

    try {
      const createResponse = (await postWithAuth(
        "/api/note/block/batch-create",
        mergedPayload,
      )) as {
        success: true;
        updatedAt?: string;
        pages?: Array<{ _id: string; uniqueId?: number; databaseProperties?: Record<string, unknown>; }>;
      } | undefined;

      // Mark all ops in this batch as synced (single transaction)
      await markOpsSynced(groupOps.map((op) => op.id));

      // The datasource now exists server-side — end its creation shield
      if (dataSourceDetail?._id) clearDatasourceCreating(String(dataSourceDetail._id));

      // Merge server-stamped fields onto the local blocks. The server writes
      // BOTH the canonical (`value.uniqueId`) and the mirror under
      // `value.databaseProperties[propertyId]`.
      if (Array.isArray(createResponse?.pages) && createResponse.pages.length > 0) {
        await Promise.all(
          createResponse.pages.map((p) =>
            p?._id
              ? mergeServerStampedDatabaseProperties(
                  p._id, p.databaseProperties ?? {}, p.uniqueId,).catch(
                    (err) =>
                  console.warn(
                    "[SyncWatcher][create] mergeServerStampedDatabaseProperties failed (non-fatal):",
                    err,
                  ),
                )
              : Promise.resolve(),
          ),
        );
      }

      // Mark every created child block + the parent synced in ONE transaction —
      // per-block updates fire a Dexie liveQuery re-render each, which is fatal
      // for a large paste. One transaction = one notification.
      const childIds = groupOps.filter((op) => !!op.noteId).map((op) => op.noteId!);
      await markBlocksSynced(
        firstPayload.parentId ? [...childIds, firstPayload.parentId] : childIds,
      );

      // Refresh pageCache.lastKnownServerUpdatedAt for each created page (one
      // transaction) so the next reload's meta-check sees IDB is already current.
      if (createResponse?.updatedAt) {
        const serverUpdatedAt = new Date(createResponse.updatedAt).getTime();
        if (!isNaN(serverUpdatedAt)) {
          await refreshPageCacheTimestamps(childIds, serverUpdatedAt);
        } else {
          console.warn(
            `[SyncWatcher][create] invalid serverUpdatedAt for batch under parent=${firstPayload.parentId} ` +
            `— skipping pageCache refresh. raw value=${JSON.stringify(createResponse.updatedAt)}`
          );
        }
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error(
        `[SyncWatcher] Failed to batch-create ${groupOps.length} page(s) under parent ${firstPayload.parentId}:`,
        errorMsg,
      );

      // All-or-nothing failure: record on every op so they retry together next cycle.
      for (const op of groupOps) {
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
        if (op.noteId) {
          await recordBlockSyncFailure(op.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Block Ops ───────────────────────────────────────────────

  private async processBlockOps(ops: SyncQueueRecord[]): Promise<void> {
    // [DUP-DEBUG] BLOCK_CHANGES batch about to fan out into note groups.
    // Lots of these arriving right after a page-load means the BlockTracker
    // emitted creates/updates for hydrated content.
    const groups = groupOpsByNoteId(ops);

    // Process up to MAX_CONCURRENT note groups in parallel
    // groupOpsByNoteId now keys by (noteId::parentId) — all keys are strings
    const noteIds = Array.from(groups.keys());
    const batches = this.chunk(noteIds, MAX_CONCURRENT);

    for (const batch of batches) {
      await Promise.all(
        batch.map((noteId) => {
          const noteOps = groups.get(noteId) ?? [];
          return this.syncBlockGroup(noteId, noteOps);
        })
      );
    }
  }

  private async syncBlockGroup(
    noteId: string,
    ops: SyncQueueRecord[]
  ): Promise<void> {
    // Tombstone ops that have already exhausted retries so they stop waking the loop
    const expiredIds = ops.filter((op) => op.attempts >= MAX_ATTEMPTS).map((op) => op.id);
    if (expiredIds.length > 0) await this.deadLetterOps(expiredIds);
    const eligible = ops.filter((op) => op.attempts < MAX_ATTEMPTS);
    if (eligible.length === 0) return;

    // Merge all ops for this note into one payload
    const merged = mergeBlockOps(eligible);
    if (!merged) return;

    // Skip if nothing to send
    if (
      merged.creates.length === 0 &&
      merged.updates.length === 0 &&
      merged.deletes.length === 0 &&
      merged.move === null
    ) {
      const opIds = eligible.map((op) => op.id);
      await markOpsSynced(opIds);
      return;
    }

    try {
      // Re-read each updated block from IDB at SEND time, not enqueue time.
      // The op's `u.data` is a snapshot from when the mutation enqueued — if a
      // BLOCK_CREATE op completed between then and now (which races whenever
      // the user starts editing a page within ~milliseconds of creating it),
      // the server-stamped `databaseProperties[uidPropId]` has been merged
      // into IDB but is NOT in the snapshot. Sending the stale snapshot would
      // wipe the just-stamped unique-id. Re-reading guarantees we ship the
      // freshest value.
      const refreshedUpdates = await Promise.all(
        merged.updates.map(async (u) => {
          const fresh = await getBlock(u.blockId);
          return {
            _id: u.blockId,
            content: fresh?.value ?? u.data,
          };
        }),
      );

      // Transform BlockChangesPayload → batch-update API shape
      // API expects: { parentId, workspaceId, blocks: [{ _id, content }], blockIdArray? }
      const apiPayload = {
        parentId: merged.parentId,
        workspaceId: merged.workspaceId,
        blocks: [
          ...merged.creates.map((c) => ({ _id: c.blockId, content: c.content })),
          ...refreshedUpdates,
        ],
        ...(merged.move?.blockIdArray ? { blockIdArray: merged.move.blockIdArray } : {}),
      };

      // The API will return { success: true, updatedAt: "<ISO string>" } once the
      // backend is updated. We capture it here and write it to pageCache so that
      // Effect A can skip a full re-fetch the next time this page is opened.
      const syncResponse = await postWithAuth("/api/note/block/batch-update", apiPayload) as
        | { success: true; updatedAt?: string }
        | undefined;

      // Mark all ops in this group as synced
      const opIds = eligible.map((op) => op.id);
      await markOpsSynced(opIds);

      // Mark the note block, its modified children, and the parent synced — in
      // ONE transaction. Per-block markBlockSynced fans out to N liveQuery
      // re-renders and freezes the main thread on large edits.
      const childBlockIds = [
        ...merged.creates.map((c) => c.blockId),
        ...merged.updates.map((u) => u.blockId),
      ];
      await markBlocksSynced([
        ...new Set([
          noteId,
          ...childBlockIds,
          ...(merged.parentId ? [merged.parentId] : []),
        ]),
      ]);

      // ── Refresh pageCache timestamp ──────────────────────────────────────
      // merged.noteId is the actual page _id (not the composite groupOpsByNoteId key).
      // If the API returned the page's new updatedAt, write it into pageCache so that
      // the next navigation to this page can compare against it and skip a full fetch.
      // Fire-and-forget: never awaited, never blocks the sync cycle.
      if (syncResponse?.updatedAt && merged.noteId) {
        const serverUpdatedAt = new Date(syncResponse.updatedAt).getTime();
        if (!isNaN(serverUpdatedAt)) {
          // Writes lastKnownServerUpdatedAt as well as the display stamp, so
          // this stays ONE read-modify-write on the row. Pairing it with a
          // refreshPageCacheTimestamp call raced: both read the same record and
          // the later put carried the other's stale value.
          //
          // This is the one sync path keyed by the page's own id, so the
          // timestamp really is this page's edit time. No author — we have no
          // user context here; the reader infers a local edit from
          // lastEditedAt running past lastEditedByAt.
          recordPageEditStamp(merged.noteId, serverUpdatedAt).catch((err) =>
            console.warn("[SyncWatcher][update] recordPageEditStamp failed (non-fatal):", err)
          );
        } else {
          console.warn(
            `[SyncWatcher][update] invalid serverUpdatedAt for page=${merged.noteId} ` +
            `— skipping pageCache refresh. raw value=${JSON.stringify(syncResponse.updatedAt)}`
          );
        }
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error(`[SyncWatcher] Failed to sync note ${noteId}:`, errorMsg);

      // Record failure on each op and schedule retry
      for (const op of eligible) {
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }

      await recordBlockSyncFailure(noteId, errorMsg);
    }
  }

  // ─── Block Delete Ops ────────────────────────────────────────

  /**
   * Processes BLOCK_DELETE ops by batching same-workspace ops into a single
   * /api/note/block/delete/batch-permanently-delete call.
   *
   * Algorithm:
   *   1. Filter eligible (attempts < MAX_ATTEMPTS) and sort by createdAt ASC.
   *   2. Group by workspaceId (the backend rejects mixed-workspace batches).
   *   3. Chunk each group at MAX_BATCH_BLOCKS so a single failed batch only
   *      stalls ~35 ops at a time (matches the create-batch cadence).
   *   4. Send chunks strictly sequentially per workspace — never two in flight.
   *   5. Backend returns { failedIds, updatedAt }. We mark only the ops whose
   *      blockId is NOT in failedIds as synced; the rest are scheduled for retry.
   *      permanentDelete is idempotent on the backend, so a stale duplicate
   *      delete from a retried op no-ops safely.
   */
  private async processBlockDeleteOps(ops: SyncQueueRecord[]): Promise<void> {
    // Tombstone any ops that already exhausted retries so they stop waking the loop
    const expiredIds = ops.filter((op) => op.attempts >= MAX_ATTEMPTS).map((op) => op.id);
    if (expiredIds.length > 0) await this.deadLetterOps(expiredIds);
    const eligible = ops
      .filter((op) => op.attempts < MAX_ATTEMPTS)
      .sort((a, b) => a.createdAt - b.createdAt);
    if (eligible.length === 0) return;

    // Group by workspaceId. An op missing workspaceId is sent on its own —
    // the request will fail validation, the op records the failure and
    // retries (matches existing per-op behaviour).
    const groups = new Map<string, SyncQueueRecord[]>();
    for (const op of eligible) {
      const payload = op.payload as BlockDeletePayload;
      const key = payload.workspaceId || `__noworkspace_${op.id}`;
      const list = groups.get(key);
      if (list) list.push(op);
      else groups.set(key, [op]);
    }

    for (const [workspaceId, groupOps] of groups) {
      // Chunk so a single failed batch doesn't stall everything.
      for (const chunk of this.chunk(groupOps, MAX_BATCH_BLOCKS)) {
        // Build a deduped blockId list for the request, but keep a map back to
        // each op so we know which ops to mark synced vs. retry.
        const opsByBlockId = new Map<string, SyncQueueRecord[]>();
        for (const op of chunk) {
          const payload = op.payload as BlockDeletePayload;
          if (!payload.blockId) continue;
          const arr = opsByBlockId.get(payload.blockId);
          if (arr) arr.push(op);
          else opsByBlockId.set(payload.blockId, [op]);
        }
        const blockIds = Array.from(opsByBlockId.keys());
        if (blockIds.length === 0) continue;

        let response:
          | {
              success: boolean;
              deleted: number;
              failed: number;
              failedIds?: string[];
              updatedAt?: string;
            }
          | undefined;

        try {
          response = (await deleteWithAuth(
            `/api/note/block/delete/batch-permanently-delete`,
            {
              body: JSON.stringify({ blockIds, workspaceId }),
            },
          )) as typeof response;
        } catch (err) {
          // Transport-level failure: nothing succeeded. Fail every op in the
          // chunk and schedule retry.
          const errorMsg = err instanceof Error ? err.message : String(err);
          console.error(
            `[SyncWatcher] batch-permanently-delete failed (workspace=${workspaceId}):`,
            errorMsg,
          );
          for (const op of chunk) {
            await recordOpFailure(op.id, errorMsg);
            retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
            if (op.noteId) {
              await recordBlockSyncFailure(op.noteId, errorMsg);
            }
          }
          continue;
        }

        const failedIdSet = new Set(response?.failedIds ?? []);
        // If the backend response is missing `failedIds` but reports failures
        // (older backend, or unexpected shape), be conservative: treat the
        // whole chunk as failed so we don't lose ops. Idempotent retry.
        const conservativeFail =
          (response?.failed ?? 0) > 0 && !response?.failedIds;

        const syncedOpIds: string[] = [];
        const syncedBlockIds: string[] = [];
        const parentPageIds = new Set<string>();

        for (const [blockId, opsForBlock] of opsByBlockId) {
          const didFail = conservativeFail || failedIdSet.has(blockId);
          if (didFail) {
            const errorMsg = conservativeFail
              ? "batch delete reported failures without failedIds (treating as failed)"
              : "batch delete reported this blockId as failed";
            for (const op of opsForBlock) {
              await recordOpFailure(op.id, errorMsg);
              retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
              if (op.noteId) {
                await recordBlockSyncFailure(op.noteId, errorMsg);
              }
            }
            continue;
          }
          // Success.
          for (const op of opsForBlock) {
            syncedOpIds.push(op.id);
            if (op.noteId) parentPageIds.add(op.noteId);
          }
          syncedBlockIds.push(blockId);
        }

        if (syncedOpIds.length > 0) {
          await markOpsSynced(syncedOpIds);
        }

        // Mark deleted blocks (and their parents) as synced — collect ids and
        // mark in one transaction to avoid per-block liveQuery re-renders.
        const deleteMarkIds = new Set<string>();
        for (const blockId of syncedBlockIds) {
          deleteMarkIds.add(blockId);
          const deletedBlock = await getBlock(blockId);
          if (deletedBlock?.parentId) deleteMarkIds.add(deletedBlock.parentId);
        }
        if (deleteMarkIds.size > 0) {
          await markBlocksSynced([...deleteMarkIds]);
        }

        // Refresh pageCache for every distinct parent page touched by this
        // chunk. Single batch-level updatedAt is good enough — pageCache
        // freshness is approximate.
        //
        // recordPageEditStamp rather than refreshPageCacheTimestamp: these ids
        // are pages (BLOCK_DELETE carries noteId separately from the deleted
        // blockId), and deleting a block is an edit to the page that held it.
        const rawUpdatedAt = response?.updatedAt;
        if (rawUpdatedAt && parentPageIds.size > 0) {
          const serverUpdatedAt = new Date(rawUpdatedAt).getTime();
          if (!isNaN(serverUpdatedAt)) {
            for (const parentId of parentPageIds) {
              recordPageEditStamp(parentId, serverUpdatedAt).catch((err) =>
                console.warn(
                  "[SyncWatcher][delete] recordPageEditStamp failed (non-fatal):",
                  err,
                ),
              );
            }
          } else {
            console.warn(
              `[SyncWatcher][delete] invalid serverUpdatedAt — skipping pageCache refresh. ` +
                `raw value=${JSON.stringify(rawUpdatedAt)}`,
            );
          }
        }
      }
    }
  }

  // ─── Block Trash Ops ─────────────────────────────────────────

  private async processBlockTrashOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as BlockDeletePayload;
        await deleteWithAuth(`/api/note/block/delete/trash`, {
          body: JSON.stringify(payload),
        });

        await markOpsSynced([op.id]);
        if (payload.blockId) {
          await markBlockSynced(payload.blockId);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to trash block ${op.noteId}:`, errorMsg);
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }
    }
  }

  // ─── Reminder Ops ────────────────────────────────────────────

  /**
   * Waits while the host block still has an unsynced create — a row pointing at
   * a block the scheduler cannot find is silently dropped. Only the LAST op per
   * reminderId is sent: the server keys on it, so earlier edits are noise.
   */
  private async processReminderOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    const { appDB } = await import("../db/client");
    const pendingCreateBlockIds = new Set<string>();
    try {
      const pendingCreates = await appDB.syncQueue
        .where("isSync")
        .equals(0)
        .and((o) => o.operationType === "BLOCK_CREATE")
        .toArray();
      for (const c of pendingCreates) if (c.noteId) pendingCreateBlockIds.add(c.noteId);
    } catch {
      // Best-effort: if the lookup fails, try the send anyway.
    }

    const lastForReminder = new Map<string, SyncQueueRecord>();
    for (const op of sorted) {
      const rid = (op.payload as { reminderId?: string }).reminderId;
      if (rid) lastForReminder.set(rid, op);
    }
    const superseded = sorted.filter((op) => {
      const rid = (op.payload as { reminderId?: string }).reminderId;
      return rid ? lastForReminder.get(rid)?.id !== op.id : false;
    });
    if (superseded.length > 0) await markOpsSynced(superseded.map((o) => o.id));

    for (const op of lastForReminder.values()) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      if (op.operationType === "REMINDER_UPSERT") {
        const payload = op.payload as ReminderUpsertPayload;
        // The block is not on the server yet — try again next cycle.
        if (payload.blockId && pendingCreateBlockIds.has(payload.blockId)) continue;
        if (payload.noteId && pendingCreateBlockIds.has(payload.noteId)) continue;
      }

      try {
        if (op.operationType === "REMINDER_CANCEL") {
          const payload = op.payload as ReminderCancelPayload;
          await deleteWithAuth(`/api/reminder/${payload.reminderId}`);
        } else {
          await postWithAuth(`/api/reminder/upsert`, op.payload);
        }
        await markOpsSynced([op.id]);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync reminder:`, errorMsg);
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }
    }
  }

  // ─── Block Restore Ops ───────────────────────────────────────

  private async processBlockRestoreOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as BlockDeletePayload;
        await postWithAuth(`/api/note/block/delete/restore`, {
          blockId: payload.blockId,
          workspaceId: payload.workspaceId,
        });

        await markOpsSynced([op.id]);
        if (payload.blockId) {
          await markBlockSynced(payload.blockId);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to restore block ${op.noteId}:`, errorMsg);
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }
    }
  }

  // ─── Page Move Ops ───────────────────────────────────────────

  /**
   * Processes PAGE_MOVE ops in createdAt order.
   * Each op posts to /api/note/block/drag-and-drop — the endpoint that moves a page
   * between public/private/restricted sections and updates parentType/parentId in MongoDB.
   */
  private async processPageMoveOps(
    ops: SyncQueueRecord[],
    cancelledCreateBlockIds: Set<string> = new Set(),
  ): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    // Blocks that still have a pending (unsynced) BLOCK_CREATE. The drag-and-drop
    // endpoint throws "Block to move not found" / "Parent not found" if the moved
    // block or its target parent isn't on the server yet — common with nested
    // blocks created-then-dragged before their create synced (e.g. the create
    // failed this cycle). Defer such moves to a later cycle instead of failing.
    const { appDB } = await import("../db/client");
    const pendingCreateBlockIds = new Set<string>();
    try {
      const pendingCreates = await appDB.syncQueue
        .where("isSync")
        .equals(0)
        .and((o) => o.operationType === "BLOCK_CREATE")
        .toArray();
      for (const c of pendingCreates) if (c.noteId) pendingCreateBlockIds.add(c.noteId);
    } catch {
      // If the lookup fails, fall through (best-effort guard).
    }

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      const movePayload = op.payload as PageMovePayload;
      // Check EVERY moved block (a multi-block reparent carries them in
      // updatedBlockInfoArray; single moves in updatedBlockInfo). If any moved
      // block or its target parent still has an unsynced create, defer the whole
      // move so it can't hit the server before those blocks exist.
      const movedInfos =
        movePayload.updatedBlockInfoArray && movePayload.updatedBlockInfoArray.length > 0
          ? movePayload.updatedBlockInfoArray
          : movePayload.updatedBlockInfo
            ? [movePayload.updatedBlockInfo]
            : [];
      const blockedByPendingCreate = movedInfos.some(
        (m) =>
          (m.blockId && pendingCreateBlockIds.has(m.blockId)) ||
          (m.newParentId && pendingCreateBlockIds.has(m.newParentId)),
      );
      if (blockedByPendingCreate) {
        // A moved block or its target parent hasn't been created on the server
        // yet — leave this op queued; it'll retry next cycle after the create lands.
        console.log(
          `[SyncWatcher] Deferring PAGE_MOVE — a moved block or its parent create hasn't synced yet`,
        );
        continue;
      }

      // Drop any moved block whose create was CANCELLED this cycle (created AND
      // deleted before syncing) — it'll never reach the server. A *pending* create
      // is deferred above instead.
      const validMovedInfos = movedInfos.filter(
        (m) => !cancelledCreateBlockIds.has(m.blockId) && !cancelledCreateBlockIds.has(m.newParentId),
      );

      // The move only concerned cancelled blocks → nothing real left to send.
      if (validMovedInfos.length === 0) {
        await markOpsSynced([op.id]);
        continue;
      }

      // LEAN payload — just the moved block(s) + destination + position. No
      // per-parent arrays to scrub: the server $pulls each block from its current
      // parent and $pushes it after insertAfterBlockId, touching only that id.
      const movePayloadToSend: PageMovePayload = {
        updatedBlockInfo: validMovedInfos[0]!,
        ...(movePayload.updatedBlockInfoArray && movePayload.updatedBlockInfoArray.length > 0
          ? { updatedBlockInfoArray: validMovedInfos }
          : {}),
      };

      try {
        // workspaceId rides alongside the lean payload — the server needs it for
        // the permission check and for resolving workspace-root section lists.
        const payload = { ...movePayloadToSend, workspaceId: op.workspaceId || this.workspaceId || "" };
        type StampedBlock = {
          _id: string;
          uniqueId?: number;
          databaseProperties: Record<string, unknown>;
          unsetPropertyId?: string;
        };
        const moveResponse = await postWithAuth("/api/note/block/drag-and-drop", payload) as
          | {
              success: true;
              updatedAt?: string;
              stampedBlock?: StampedBlock;
              stampedBlocks?: StampedBlock[];
              failedBlockIds?: string[];
              stampedPageIds?: string[];
            }
          | undefined;

        await markOpsSynced([op.id]);

        // A multi-block move can partially succeed: the backend moves what it can
        // and reports any it couldn't (e.g. a concurrently-deleted block). The op
        // is still marked synced (the rest moved); surface the skipped ones — they
        // reconcile to the server's state on the next page load.
        if (moveResponse?.failedBlockIds && moveResponse.failedBlockIds.length > 0) {
          console.warn(
            "[SyncWatcher][move] some blocks could not be moved (skipped, will reconcile on reload):",
            moveResponse.failedBlockIds,
          );
        }

        // Drag into a (different) datasource re-stamps the unique-id under
        // the target datasource's propertyId. Merge the new stamp into IDB
        // and dispatch the context-refresh event so the ID column re-renders
        // with the new TASK-N immediately. Old datasource's mirror is removed
        // via `unsetPropertyId` to keep `databaseProperties` clean. The
        // canonical at `value.uniqueId` is overwritten to the new counter
        // since the page is now scoped to a different datasource.
        //
        // A multi-block move returns `stampedBlocks` (one per moved block);
        // single-block moves return `stampedBlock`. Merge every stamp we got.
        const stamped: StampedBlock[] =
          moveResponse?.stampedBlocks && moveResponse.stampedBlocks.length > 0
            ? moveResponse.stampedBlocks
            : moveResponse?.stampedBlock
              ? [moveResponse.stampedBlock]
              : [];
        for (const sb of stamped) {
          if (!sb?._id) continue;
          await mergeServerStampedDatabaseProperties(
            sb._id,
            sb.databaseProperties,
            sb.uniqueId,
            sb.unsetPropertyId,
          ).catch((err) =>
            console.warn(
              "[SyncWatcher][move] mergeServerStampedDatabaseProperties failed (non-fatal):",
              err,
            ),
          );
        }

        // Mark the moved block(s) and their destination parent(s) synced in one
        // transaction (avoids per-block liveQuery re-renders on multi-block moves).
        const moveMarkIds = new Set<string>();
        if (op.noteId) moveMarkIds.add(op.noteId);
        if (payload.updatedBlockInfo?.newParentId) moveMarkIds.add(payload.updatedBlockInfo.newParentId);
        for (const m of payload.updatedBlockInfoArray ?? []) {
          if (m.newParentId) moveMarkIds.add(m.newParentId);
        }
        if (moveMarkIds.size > 0) {
          await markBlocksSynced([...moveMarkIds]);
        }

        // Advance the display stamp on whichever pages the move actually
        // changed. The server names them because we can't: this op's noteId is
        // the moved BLOCK, and a move touches the page it left and the page it
        // landed in — often the same one, already deduped server-side.
        if (moveResponse?.updatedAt && moveResponse.stampedPageIds?.length) {
          const editedAt = new Date(moveResponse.updatedAt).getTime();
          if (!isNaN(editedAt)) {
            for (const pageId of moveResponse.stampedPageIds) {
              recordPageEditStamp(pageId, editedAt).catch((err) =>
                console.warn("[SyncWatcher][move] recordPageEditStamp failed (non-fatal):", err)
              );
            }
          }
        }

        // Refresh pageCache for the moved block's page
        if (moveResponse?.updatedAt && op.noteId) {
          const serverUpdatedAt = new Date(moveResponse.updatedAt).getTime();
          if (!isNaN(serverUpdatedAt)) {
            refreshPageCacheTimestamp(op.noteId, serverUpdatedAt).catch((err) =>
              console.warn("[SyncWatcher][move] refreshPageCacheTimestamp failed (non-fatal):", err)
            );
          } else {
            console.warn(
              `[SyncWatcher][move] invalid serverUpdatedAt for page=${op.noteId} ` +
              `— skipping pageCache refresh. raw value=${JSON.stringify(moveResponse.updatedAt)}`
            );
          }
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to move page ${op.noteId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());

        if (op.noteId) {
          await recordBlockSyncFailure(op.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Sidebar Ops ─────────────────────────────────────────────

  private async processSidebarOps(ops: SyncQueueRecord[]): Promise<void> {
    // Take only the most recent sidebar op — older ones are superseded
    const sorted = [...ops].sort((a, b) => b.createdAt - a.createdAt);
    if (!sorted[0]) return;
    const latest = sorted[0];
    const allOpIds = ops.map((op) => op.id);

    try {
      await postWithAuth("/api/workspace/sidebar-order", latest?.payload);

      await markOpsSynced(allOpIds);
      await markSidebarOrderSynced();

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error("[SyncWatcher] Failed to sync sidebar order:", errorMsg);

      await recordOpFailure(latest.id, errorMsg);
      retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());
    }
  }

  // ─── Work Area Ops ───────────────────────────────────────────

  private async processWorkAreaOps(ops: SyncQueueRecord[]): Promise<void> {
    // Process work area ops sequentially in createdAt order
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        await this.syncWorkAreaOp(op);
        await markOpsSynced([op.id]);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync workarea op ${op.operationType}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());

        const payload = op.payload as any;
        if (payload.workAreaId) {
          await recordWorkAreaSyncFailure(payload.workAreaId, errorMsg);
        }
      }
    }
  }

  private async syncWorkAreaOp(op: SyncQueueRecord): Promise<void> {
    const payload = op.payload as any;

    switch (op.operationType) {
      case "WORKAREA_CREATE":
        await postWithAuth("/api/work-area/create", payload);
        break;
      case "WORKAREA_UPDATE":
        await postWithAuth(`/api/workArea/${payload.workAreaId}`, payload);
        break;
      case "WORKAREA_DELETE":
        await postWithAuth(`/api/workArea/${payload.workAreaId}/delete`, payload);
        if (payload.workAreaId) {
          await markWorkAreaSynced(payload.workAreaId);
        }
        break;
    }

    if (payload.workAreaId) {
      await markWorkAreaSynced(payload.workAreaId);
    }
  }

  // ─── Data Source Ops ─────────────────────────────────────────

  /**
   * Processes DATASOURCE_UPDATE ops in createdAt order.
   * For each datasource, only the most recent op matters — earlier ones are superseded.
   * Calls /api/database/updateDataSource/:dataSourceId with the merged updates.
   */
  private async processDataSourceOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by dataSourceId so we can take the latest per datasource
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as DataSourceUpdatePayload;
      const dsId = payload.dataSourceId;
      if (!grouped.has(dsId)) grouped.set(dsId, []);
      grouped.get(dsId)!.push(op);
    }

    for (const [dataSourceId, dsOps] of grouped) {
      // Take only the most recent op — it contains the latest state
      const sorted = [...dsOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = dsOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as DataSourceUpdatePayload;
        // Map updates to the shape expected by /api/database/update
        await postWithAuth("/api/database/update", {
          blockId: payload.blockId,
          dataSourceId: payload.dataSourceId,
          ...payload.updates,
        });

        await markOpsSynced(allOpIds);
        await markDataSourceSynced(dataSourceId);

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync datasource ${dataSourceId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());
        await recordDataSourceSyncFailure(dataSourceId, errorMsg);
      }
    }
  }

  // ─── View Update Ops ─────────────────────────────────────────

  /**
   * Processes VIEW_UPDATE ops (rename/icon change).
   * Groups by viewTypeId and takes the latest op for each.
   * Calls /api/database/update-view-type.
   */
  private async processViewUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by viewTypeId to merge multiple updates
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as ViewUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      // Take only the most recent op
      const sorted = [...viewOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        // Merge coalesced ops field-wise (newest wins per field) so a queued
        // form edit isn't dropped when a later rename targets the same view.
        const merged = {} as ViewUpdatePayload;
        for (const op of [...sorted].reverse()) {
          for (const [key, value] of Object.entries(op.payload as ViewUpdatePayload)) {
            if (value !== undefined) (merged as any)[key] = value;
          }
        }
        await postWithAuth("/api/database/update-view-type", {
          blockId: merged.blockId,
          viewTypeId: merged.viewTypeId,
          title: merged.title,
          icon: merged.icon,
          viewType: merged.viewType,
          formQuestions: merged.formQuestions,
          formTitle: merged.formTitle,
          formDescription: merged.formDescription,
          formIcon: merged.formIcon,
          formCoverImage: merged.formCoverImage,
          formCoverPosition: merged.formCoverPosition,
          isPublicForm: merged.isPublicForm,
          formAnonymousResponses: merged.formAnonymousResponses,
        });

        await markOpsSynced(allOpIds);

        // Mark the collection_view block as synced if noteId is present
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync view update ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── View Reorder Ops ────────────────────────────────────────

  /** Groups VIEW_REORDER by blockId; the latest order wins. */
  private async processViewReorderOps(ops: SyncQueueRecord[]): Promise<void> {
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const blockId = (op.payload as ViewReorderPayload).blockId;
      if (!grouped.has(blockId)) grouped.set(blockId, []);
      grouped.get(blockId)!.push(op);
    }

    for (const [blockId, blockOps] of grouped) {
      const sorted = [...blockOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = blockOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as ViewReorderPayload;
        await postWithAuth("/api/database/re-order-views", {
          blockId: payload.blockId,
          order: payload.order,
        });

        await markOpsSynced(allOpIds);
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync view reorder ${blockId}:`, errorMsg);
        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());
        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Layout Update Ops ───────────────────────────────────────

  /**
   * Processes LAYOUT_UPDATE ops.
   * Groups by viewTypeId and merges multiple layout settings changes.
   * Calls /api/database/settings/layout.
   */
  private async processLayoutUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by viewTypeId to merge multiple updates
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as LayoutUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      // Sort by createdAt ASC to merge in order
      const sorted = [...viewOps].sort((a, b) => a.createdAt - b.createdAt);
      const latest = sorted[sorted.length - 1];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as LayoutUpdatePayload;
        const blockId = payload.blockId;

        // Merge all layout settings
        let mergedLayoutSettings = {};
        for (const op of sorted) {
          const payload = op.payload as LayoutUpdatePayload;
          mergedLayoutSettings = { ...mergedLayoutSettings, ...payload.layoutSettings };
        }

        await postWithAuth("/api/database/settings/layout", {
          blockId,
          viewTypeId,
          layoutSettings: mergedLayoutSettings,
        });

        await markOpsSynced(allOpIds);

        // Mark the collection_view block as synced if noteId is present
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync layout update ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Property Visibility Ops ─────────────────────────────────

  /**
   * Processes PROPERTY_VISIBILITY_UPDATE ops.
   * Groups by viewTypeId and takes the latest visibility state.
   * Calls /api/database/settings/property-visibility.
   */
  private async processPropertyVisibilityUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by viewTypeId to merge multiple updates
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as PropertyVisibilityUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      // Take only the most recent op
      const sorted = [...viewOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as PropertyVisibilityUpdatePayload;
        await postWithAuth("/api/database/settings/property-visibility", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          propertyVisibility: payload.propertyVisibility,
        });

        await markOpsSynced(allOpIds);

        // Mark the collection_view block as synced if noteId is present
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync property visibility ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Filter Update Ops ───────────────────────────────────────

  /**
   * Processes FILTER_UPDATE ops.
   * Groups by viewTypeId and takes the latest filter state.
   * Calls /api/database/settings/filter.
   */
  private async processFilterUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by viewTypeId to merge multiple updates
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as FilterUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      // Take only the most recent op
      const sorted = [...viewOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as FilterUpdatePayload;
        await postWithAuth("/api/database/settings/filter", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          filters: payload.filters,
        });

        await markOpsSynced(allOpIds);

        // Mark the collection_view block as synced if noteId is present
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync filters ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Sort Update Ops ─────────────────────────────────────────

  /**
   * Processes SORT_UPDATE ops.
   * Groups by viewTypeId and takes the latest sort state.
   * Calls /api/database/settings/sort.
   */
  private async processSortUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Group by viewTypeId to merge multiple updates
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as SortUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      // Take only the most recent op
      const sorted = [...viewOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as SortUpdatePayload;
        await postWithAuth("/api/database/settings/sort", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          sorts: payload.sorts,
        });

        await markOpsSynced(allOpIds);

        // Mark the collection_view block as synced if noteId is present
        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync sorts ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }
  // ─── Column Width Ops ────────────────────────────────────────

  /**
   * Processes COLUMN_WIDTHS_UPDATE ops.
   * Each payload carries the view's full width map, so per view only the
   * latest op matters. Calls /api/database/settings/column-widths.
   */
  private async processColumnWidthsOps(ops: SyncQueueRecord[]): Promise<void> {
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as ColumnWidthsUpdatePayload;
      const viewId = payload.viewTypeId;
      if (!grouped.has(viewId)) grouped.set(viewId, []);
      grouped.get(viewId)!.push(op);
    }

    for (const [viewTypeId, viewOps] of grouped) {
      const sorted = [...viewOps].sort((a, b) => b.createdAt - a.createdAt);
      const latest = sorted[0];
      if (!latest) continue;
      const allOpIds = viewOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        const payload = latest.payload as ColumnWidthsUpdatePayload;
        await postWithAuth("/api/database/settings/column-widths", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          columnWidths: payload.columnWidths,
        });

        await markOpsSynced(allOpIds);

        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync column widths ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Property Create Ops ─────────────────────────────────────

  /**
   * Processes DATASOURCE_PROPERTY_CREATE ops in createdAt order.
   * Posts to /api/database/create-property and updates IDB with the
   * server response — important for relation types where the server
   * assigns the reverse property and paired datasource.
   */
  private async processPropertyCreateOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as PropertyCreatePayload;

        const res = await postWithAuth("/api/database/create-property", {
          propertyId: payload.propertyId,
          dataSourceId: payload.dataSourceId,
          blockId: payload.blockId,
          name: payload.name,
          type: payload.type,
          options: payload.options,
          ...(payload.groups ? { groups: payload.groups } : {}),
          ...(payload.specialProperty ? { specialProperty: true } : {}),
          ...(payload.linkedDatabaseId ? { linkedDatabaseId: payload.linkedDatabaseId } : {}),
          ...(payload.relationLimit ? { relationLimit: payload.relationLimit } : {}),
          ...(payload.twoWayRelation !== undefined ? { twoWayRelation: payload.twoWayRelation } : {}),
          ...(payload.syncedPropertyId ? { syncedPropertyId: payload.syncedPropertyId } : {}),
          ...(payload.syncedPropertyName ? { syncedPropertyName: payload.syncedPropertyName } : {}),
          ...(payload.rollup ? { rollup: payload.rollup } : {}),
          ...(payload.automationId ? { automationId: payload.automationId } : {}),
          ...(payload.icon ? { icon: payload.icon } : {}),
          ...(payload.formula !== undefined ? { formula: payload.formula } : {}),
          ...(payload.formulaReturnType ? { formulaReturnType: payload.formulaReturnType } : {}),
          ...(payload.timeFormat ? { timeFormat: payload.timeFormat } : {}),
          ...(payload.timeZone !== undefined ? { timeZone: payload.timeZone } : {}),
          ...(payload.numberFormat ? { numberFormat: payload.numberFormat } : {}),
          ...(payload.decimalPlaces !== undefined ? { decimalPlaces: payload.decimalPlaces } : {}),
          ...(payload.showAs ? { showAs: payload.showAs } : {}),
          ...(payload.progressColor ? { progressColor: payload.progressColor } : {}),
          ...(payload.progressDivideBy !== undefined ? { progressDivideBy: payload.progressDivideBy } : {}),
          ...(payload.showNumberText !== undefined ? { showNumberText: payload.showNumberText } : {}),
          ...(payload.insertAfterPropertyId ? { insertAfterPropertyId: payload.insertAfterPropertyId } : {}),
        }) as any;

        // For relation types the server returns the authoritative datasource
        // (with the real syncedPropertyId etc.) — update IDB so future reads are correct.
        if (payload.type === "relation") {
          // Import once outside condition branches to avoid dynamic import per iteration
          const { updateDataSourceInDB } = await import("../stores/dataSourcesStore");

          if (res?.dataSource) {
            const dsId = typeof res.dataSource._id === "string"
              ? res.dataSource._id
              : res.dataSource._id?.toString?.() || payload.dataSourceId;
            await updateDataSourceInDB(dsId, res.dataSource);
          }

          if (res?.reverseDataSource) {
            const reverseDsId = typeof res.reverseDataSource._id === "string"
              ? res.reverseDataSource._id
              : res.reverseDataSource._id?.toString?.();
            if (reverseDsId) {
              await updateDataSourceInDB(reverseDsId, res.reverseDataSource);
            }
          }
        }

        await markOpsSynced([op.id]);
        await markDataSourceSynced(payload.dataSourceId);

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to create property ${(op.payload as PropertyCreatePayload).propertyId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
        await recordDataSourceSyncFailure((op.payload as PropertyCreatePayload).dataSourceId, errorMsg);
      }
    }
  }

  // ─── Group Update Ops ───────────────────────────────────────

  /**
   * Processes GROUP_UPDATE ops.
   * Groups by viewTypeId and takes only the latest op per view.
   * Posts to /api/database/settings/group.
   */
  private async processGroupUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // 1. Group by viewTypeId and take latest
    const latestByView = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as GroupUpdatePayload;
      const key = payload.viewTypeId;
      // Pick the latest op for this view. createdAt is millisecond-precision so
      // ties are possible when ops are queued in the same tick — fall back to
      // op.id (UUID) lexicographic order so the winner is deterministic.
      const current = latestByView.get(key);
      if (
        !current ||
        op.createdAt > current.createdAt ||
        (op.createdAt === current.createdAt && op.id > current.id)
      ) {
        latestByView.set(key, op);
      }
    }

    // 2. Process each view
    for (const [viewTypeId, latestOp] of latestByView.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as GroupUpdatePayload).viewTypeId === viewTypeId)
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as GroupUpdatePayload;

        await postWithAuth("/api/database/settings/group", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          group: payload.group,
        });

        await markOpsSynced(idsToMark);

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync group updates for view ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());
      }
    }
  }
  // ─── Property Update Ops ─────────────────────────────────────

  /**
   * Processes DATASOURCE_SCHEMA_REORDER ops.
   * Only the latest order per datasource is worth sending.
   * Calls /api/database/re-order-schema.
   */
  private async processSchemaReorderOps(ops: SyncQueueRecord[]): Promise<void> {
    const latestByDataSource = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const { dataSourceId } = op.payload as SchemaReorderPayload;
      const current = latestByDataSource.get(dataSourceId);
      if (!current || op.createdAt > current.createdAt) latestByDataSource.set(dataSourceId, op);
    }

    for (const [dataSourceId, latestOp] of latestByDataSource.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as SchemaReorderPayload).dataSourceId === dataSourceId)
        .map((op) => op.id);

      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const { dataSourceId: dsId, blockId, viewId, order } = latestOp.payload as SchemaReorderPayload;
        await postWithAuth("/api/database/re-order-schema", { dataSourceId: dsId, blockId, viewId, order });
        await markOpsSynced(idsToMark);
        await markDataSourceSynced(dataSourceId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to reorder schema for ${dataSourceId}:`, errorMsg);
        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());
        await recordDataSourceSyncFailure(dataSourceId, errorMsg);
      }
    }
  }

  /**
   * Processes DATASOURCE_PROPERTY_UPDATE ops in createdAt order.
   * Calls /api/database/update-property-schema via postWithAuth.
   */
  private async processPropertyUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as PropertySchemaUpdatePayload;

        // Destructure to exclude internal-only fields (workspaceId) from API payload
        const { workspaceId: _ws, ...apiPayload } = payload;
        await postWithAuth("/api/database/update-property-schema", apiPayload);

        await markOpsSynced([op.id]);
        await markDataSourceSynced(payload.dataSourceId);

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        const payload = op.payload as PropertySchemaUpdatePayload;
        console.error(`[SyncWatcher] Failed to update property ${payload.propertyId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
        await recordDataSourceSyncFailure(payload.dataSourceId, errorMsg);
      }
    }
  }

  // ─── Property Delete Ops ─────────────────────────────────────

  /**
   * Processes DATASOURCE_PROPERTY_DELETE ops in createdAt order.
   * Calls /api/database/delete-property via deleteWithAuth and updates IDB with
   * the server response — important for two-way relations, where the server may
   * hard-delete the counterpart property on the paired datasource ("deleted"),
   * strip only its synced fields when it can't verify our access ("detached"), or
   * leave it untouched ("skipped": locked, or the mirror couldn't be proven).
   * Only "deleted" is what the client optimistically assumed, so the other two
   * outcomes are reconciled here — see repairUnreturnedCounterpart.
   */
  private async processPropertyDeleteOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort((a, b) => a.createdAt - b.createdAt);

    for (const op of sorted) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as PropertyDeletePayload;

        // `counterpartPolicy` carries the choice the user made in the delete
        // dialog ("delete" the paired property too, or "detach" and keep it as a
        // plain one-way relation). Forwarded only when the queued op actually has
        // it, so a delete queued before this field existed — or by any other
        // producer — still flushes with the exact body it used to send and gets
        // the server's default ("delete"). It can only ever narrow the cascade:
        // the server's identity/lock/authorization guards still decide the rest.
        const res = await deleteWithAuth("/api/database/delete-property", {
          body: JSON.stringify({
            dataSourceId: payload.dataSourceId,
            propertyId: payload.propertyId,
            blockId: payload.blockId,
            ...(payload.counterpartPolicy
              ? { counterpartPolicy: payload.counterpartPolicy }
              : {}),
          }),
        }) as any;

        // deleteWithAuth resolves with an error object instead of throwing on a
        // non-2xx response — rethrow so the catch below records the failure and
        // schedules a retry rather than marking a failed delete as synced.
        if (res?.isError) {
          // …except when the property is already gone server-side: that is the
          // outcome this op wanted, so settle it instead of burning attempts and
          // dead-lettering with a permanent syncError on the datasource.
          if (isAlreadyAbsentDeleteError(res)) {
            console.log(
              `[SyncWatcher] Property ${payload.propertyId} already absent on the server — treating delete as complete`,
            );
            await markOpsSynced([op.id]);
            await markDataSourceSynced(payload.dataSourceId);
            continue;
          }
          throw new Error(res.message || `Request failed with status ${res.status}`);
        }

        // The server returns the authoritative datasource(s) — the source plus,
        // for a two-way relation whose counterpart lives elsewhere, the paired
        // datasource with the cascaded property removed. Update IDB so the
        // counterpart column doesn't linger locally as an orphan.
        // Import once outside condition branches to avoid dynamic import per iteration
        const { updateDataSourceInDB } = await import("../stores/dataSourcesStore");

        if (res?.dataSource) {
          const dsId = typeof res.dataSource._id === "string"
            ? res.dataSource._id
            : res.dataSource._id?.toString?.() || payload.dataSourceId;
          await updateDataSourceInDB(dsId, res.dataSource);
        }

        // Applied whenever the server sends it, regardless of res.counterpart.action
        // ("deleted", "detached" or "skipped"). The server only includes it when the
        // caller was positively authorized for that datasource, and in every such
        // case it is the post-write authoritative state: the counterpart property is
        // gone on a delete, still present minus its three synced fields on a detach,
        // and fully intact on a skip. The client's optimistic guard is deliberately
        // weaker than the server's verified-mirror guard — it can't see locks,
        // permissions or cross-workspace state — so the server's copy is the only
        // source of truth: applying it wholesale RESTORES a counterpart the client
        // removed too eagerly and self-heals divergences we haven't enumerated. A
        // skip mutates nothing for other clients, so no socket event arrives to fix
        // it later.
        let counterpartDsId: string | undefined;
        if (res?.counterpartDataSource) {
          counterpartDsId = typeof res.counterpartDataSource._id === "string"
            ? res.counterpartDataSource._id
            : res.counterpartDataSource._id?.toString?.();
          if (counterpartDsId) {
            await updateDataSourceInDB(counterpartDsId, res.counterpartDataSource);
          }
        }

        await markOpsSynced([op.id]);
        await markDataSourceSynced(payload.dataSourceId);
        if (counterpartDsId) {
          await markDataSourceSynced(counterpartDsId);
        }

        // The server OMITS counterpartDataSource when the caller isn't allowed to
        // see that datasource — it must not hand its title/properties to someone
        // who failed the counterpart authorization check. But the client already
        // removed the counterpart property from its local copy optimistically, so
        // that copy is now wrong and the response carries nothing to repair it
        // with. Repair it out-of-band instead — one call site, so an authorized
        // response that we already applied above never triggers a second fetch.
        // Self-relations are excluded: both removals are already reflected in
        // `res.dataSource` above.
        //
        // What "wrong" means depends on the action, and that decides whether an
        // unreadable counterpart may be dropped locally:
        //   - "deleted"/"skipped": unchanged. The re-read either corrects the copy
        //     or proves the user can't read that datasource at all, in which case
        //     holding a divergent record is worse than holding none.
        //   - "detached": the server unset only syncedPropertyId /
        //     syncedPropertyName / twoWayRelation — the property, its type and all
        //     its cell values demonstrably SURVIVE. The re-read restores the column
        //     the client wrongly removed, now a plain one-way relation. If it fails
        //     we keep the local record: deleting a datasource whose property we know
        //     still exists would be strictly worse than a stale copy.
        const counterpartAction: string | undefined = res?.counterpart?.action;
        const reportedCounterpartId = res?.counterpart?.dataSourceId
          ? String(res.counterpart.dataSourceId)
          : undefined;
        if (
          reportedCounterpartId &&
          !counterpartDsId &&
          reportedCounterpartId !== payload.dataSourceId
        ) {
          await this.repairUnreturnedCounterpart(reportedCounterpartId, {
            dropWhenUnreadable: counterpartAction !== "detached",
          });
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        const payload = op.payload as PropertyDeletePayload;
        console.error(`[SyncWatcher] Failed to delete property ${payload.propertyId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
        await recordDataSourceSyncFailure(payload.dataSourceId, errorMsg);
      }
    }
  }

  /**
   * Re-syncs a counterpart datasource the delete-property response referred to but
   * did not include (the caller isn't authorized to receive its contents).
   *
   * The local copy has already had the counterpart property optimistically removed,
   * so leaving it alone means keeping a silently-corrupted record. Two outcomes:
   *   - the normal read path returns it → write the authoritative copy back;
   *   - the read is refused (403/404) → drop the record rather than keeping a
   *     divergent one, but ONLY when `dropWhenUnreadable` is set. It re-hydrates
   *     normally if they ever legitimately open that board again.
   * Anything else (network error, 5xx) is transient: leave the record untouched and
   * let a later hydration fix it.
   *
   * `dropWhenUnreadable` is false for a "detached" counterpart: there the server
   * kept the property and only unset its three synced fields, so the local record
   * is merely missing a column — discarding the whole datasource would destroy more
   * than it repairs. It stays true for "deleted"/"skipped", where an unreadable
   * datasource means the user shouldn't be holding the record at all.
   *
   * Only datasources ALREADY present locally are touched — we never pull a record
   * the client didn't have. This never throws: the user's own delete already
   * succeeded server-side and must not be re-queued because of a repair failure,
   * so it deliberately does not touch recordOpFailure / retryScheduler.
   */
  private async repairUnreturnedCounterpart(
    dataSourceId: string,
    options: { dropWhenUnreadable?: boolean } = {},
  ): Promise<void> {
    const { dropWhenUnreadable = true } = options;
    try {
      const { getDataSourceFromDB, updateDataSourceInDB } = await import("../stores/dataSourcesStore");

      const local = await getDataSourceFromDB(dataSourceId);
      if (!local) return;   // nothing locally to correct

      const res = await getWithAuth(`/api/database/get-data-source/${dataSourceId}`) as any;

      if (res?.isError) {
        if (dropWhenUnreadable && (res.status === 403 || res.status === 404)) {
          const { appDB } = await import("../db/client");
          await appDB.dataSources.delete(dataSourceId);
          console.warn(
            `[SyncWatcher] Counterpart datasource ${dataSourceId} is not readable (${res.status}) — dropped the stale local copy`,
          );
        } else {
          console.warn(
            `[SyncWatcher] Could not re-read counterpart datasource ${dataSourceId} (${res.status}) — leaving the local copy for a later hydration`,
          );
        }
        return;
      }

      const authoritative = res?.collection?.dataSource;
      if (!authoritative) return;

      await updateDataSourceInDB(dataSourceId, authoritative);
      await markDataSourceSynced(dataSourceId);
    } catch (err) {
      // Never fail the delete op over a repair attempt.
      console.warn(
        `[SyncWatcher] Failed to repair counterpart datasource ${dataSourceId}:`,
        err instanceof Error ? err.message : String(err),
      );
    }
  }

  // ─── View Delete Ops ────────────────────────────────────

  /**
   * Processes VIEW_DELETE ops.
   * Calls /api/database/delete-view-type for each view to delete.
   */
  private async processViewDeleteOps(ops: SyncQueueRecord[]): Promise<void> {
    for (const op of ops) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as ViewDeletePayload;

        await deleteWithAuth("/api/database/delete-view-type", {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            blockId: payload.blockId,
            viewTypeToDelete: payload.viewTypeId,
          }),
        });

        await markOpsSynced([op.id]);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync view delete for board ${(op.payload as ViewDeletePayload).blockId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }
    }
  }

  // ─── View Add Ops ───────────────────────────────────────

  /**
   * Processes VIEW_ADD ops.
   * Calls /api/database/add-view-type for each new view.
   */
  private async processViewAddOps(ops: SyncQueueRecord[]): Promise<void> {
    for (const op of ops) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as ViewAddPayload;

        await postWithAuth("/api/database/add-view-type", {
          blockId: payload.blockId,
          viewId: payload.viewId,
          typeToAdd: payload.typeToAdd,
          viewTypeValue: payload.viewTypeValue,
        });

        await markOpsSynced([op.id]);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync view add for board ${(op.payload as ViewAddPayload).blockId}:`, errorMsg);

        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
      }
    }
  }

  /**
   * Processes UPDATE_PROPERTY_VALUE ops.
   * Merges multiple updates for the same block property, taking the latest.
   * Calls /api/database/update-property-value.
   */
  private async processUpdatePropertyValueOps(ops: SyncQueueRecord[]): Promise<void> {
    // 1. Group by blockId::propertyId and take latest
    const latestItems = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as UpdatePropertyValuePayload;
      const key = `${payload.blockId}::${payload.propertyId}`;
      if (!latestItems.has(key) || op.createdAt > latestItems.get(key)!.createdAt) {
        latestItems.set(key, op);
      }
    }

    // 2. Process each unique block property update
    for (const [key, latestOp] of latestItems.entries()) {
      const [blockId, propertyId] = key.split("::");
      const idsToMark = ops
        .filter((op) => {
          const p = op.payload as UpdatePropertyValuePayload;
          return p.blockId === blockId && p.propertyId === propertyId;
        })
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as UpdatePropertyValuePayload;
        const res = (await postWithAuth("/api/database/update-property-value", payload)) as
          | { updatedAt?: string }
          | undefined;

        await markOpsSynced(idsToMark);

        // The server's edit time replaces the local guess. Display stamp only:
        // a cell edit says nothing about whether the page's content is current.
        const serverAt = res?.updatedAt ? new Date(res.updatedAt).getTime() : Number.NaN;
        if (!Number.isNaN(serverAt) && blockId) void recordLocalPageEdit(blockId, serverAt, null);

        // Mark the block itself as synced
        if (latestOp.noteId) {
          await markBlockSynced(latestOp.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync property update for ${key}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());
      }
    }
  }

  // ─── Chart Settings Update Ops ───────────────────────────────

  /**
   * Processes CHART_SETTINGS_UPDATE ops.
   * Groups by viewTypeId and takes the latest chart settings per view.
   * Calls /api/database/settings/chart.
   */
  private async processChartSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const latestByView = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as ChartSettingsUpdatePayload;
      const key = payload.viewTypeId;
      // Pick the latest op for this view. createdAt is millisecond-precision so
      // ties are possible when ops are queued in the same tick — fall back to
      // op.id (UUID) lexicographic order so the winner is deterministic.
      const current = latestByView.get(key);
      if (
        !current ||
        op.createdAt > current.createdAt ||
        (op.createdAt === current.createdAt && op.id > current.id)
      ) {
        latestByView.set(key, op);
      }
    }

    for (const [viewTypeId, latestOp] of latestByView.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as ChartSettingsUpdatePayload).viewTypeId === viewTypeId)
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as ChartSettingsUpdatePayload;
        await postWithAuth("/api/database/settings/chart", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          chartSettings: payload.chartSettings,
        });

        await markOpsSynced(idsToMark);

        if (latestOp.noteId) {
          await markBlockSynced(latestOp.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync chart settings for view ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        if (latestOp.noteId) {
          await recordBlockSyncFailure(latestOp.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Dashboard Settings Update Ops ───────────────────────────

  /**
   * Processes DASHBOARD_SETTINGS_UPDATE ops. Each op may include atomic
   * createWidget / deleteWidget alongside the layout, so we DON'T coalesce —
   * each op is posted in queue order. Without ordering, a layout that
   * references a widget could land before that widget's create.
   */
  private async processDashboardSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const ordered = [...ops].sort((a, b) =>
      a.createdAt !== b.createdAt ? a.createdAt - b.createdAt : a.id < b.id ? -1 : 1,
    );

    for (const op of ordered) {
      if (op.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps([op.id]);
        continue;
      }

      try {
        const payload = op.payload as DashboardSettingsUpdatePayload;
        await postWithAuth("/api/database/settings/dashboard", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          dashboard: payload.dashboard,
          ...(payload.createWidget ? { createWidget: payload.createWidget } : {}),
          ...(payload.deleteWidget ? { deleteWidget: payload.deleteWidget } : {}),
        });

        await markOpsSynced([op.id]);
        if (op.noteId) await markBlockSynced(op.noteId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync dashboard settings:`, errorMsg);
        await recordOpFailure(op.id, errorMsg);
        retryScheduler.schedule(op.id, op.attempts + 1, () => this.wake());
        if (op.noteId) await recordBlockSyncFailure(op.noteId, errorMsg);
      }
    }
  }

  // ─── Toggle Lock Ops ─────────────────────────────────────────

  /**
   * Processes TOGGLE_LOCK ops.
   * Groups by viewTypeId and takes the latest lock state per view.
   * Calls /api/database/settings/lock.
   */
  /**
   * Processes BLOCK_SETTINGS_UPDATE ops.
   * Groups by blockId and merges every queued patch in createdAt order, so
   * toggling two different keys offline keeps both (last-write-wins would
   * send only the final op's key and silently drop the earlier one).
   * Calls /api/database/settings/block.
   */
  private async processBlockSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const grouped = new Map<string, SyncQueueRecord[]>();
    for (const op of ops) {
      const payload = op.payload as BlockSettingsUpdatePayload;
      const blockId = payload.blockId;
      if (!grouped.has(blockId)) grouped.set(blockId, []);
      grouped.get(blockId)!.push(op);
    }

    for (const [blockId, blockOps] of grouped) {
      // Sort by createdAt ASC to merge in order
      const sorted = [...blockOps].sort((a, b) => a.createdAt - b.createdAt);
      const latest = sorted[sorted.length - 1];
      if (!latest) continue;
      const allOpIds = blockOps.map((op) => op.id);
      if (latest.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(allOpIds);
        continue;
      }

      try {
        let mergedSettings = {};
        for (const op of sorted) {
          const payload = op.payload as BlockSettingsUpdatePayload;
          mergedSettings = { ...mergedSettings, ...payload.settings };
        }

        await postWithAuth("/api/database/settings/block", {
          blockId,
          settings: mergedSettings,
        });

        await markOpsSynced(allOpIds);

        if (latest.noteId) {
          await markBlockSynced(latest.noteId);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync block settings for block ${blockId}:`, errorMsg);

        await recordOpFailure(latest.id, errorMsg);
        retryScheduler.schedule(latest.id, (latest.attempts || 0) + 1, () => this.wake());

        if (latest.noteId) {
          await recordBlockSyncFailure(latest.noteId, errorMsg);
        }
      }
    }
  }

  private async processToggleLockOps(ops: SyncQueueRecord[]): Promise<void> {
    // The lock is block-level (whole collection view), so collapse ops per block.
    const latestByBlock = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as ToggleLockPayload;
      const key = payload.blockId;
      // Pick the latest op for this block. createdAt is millisecond-precision so
      // ties are possible when ops are queued in the same tick — fall back to
      // op.id (UUID) lexicographic order so the winner is deterministic.
      const current = latestByBlock.get(key);
      if (
        !current ||
        op.createdAt > current.createdAt ||
        (op.createdAt === current.createdAt && op.id > current.id)
      ) {
        latestByBlock.set(key, op);
      }
    }

    for (const [blockId, latestOp] of latestByBlock.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as ToggleLockPayload).blockId === blockId)
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as ToggleLockPayload;
        await postWithAuth("/api/database/settings/lock", {
          blockId: payload.blockId,
          isLocked: payload.isLocked,
        });

        await markOpsSynced(idsToMark);

        if (latestOp.noteId) {
          await markBlockSynced(latestOp.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync lock for block ${blockId}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        if (latestOp.noteId) {
          await recordBlockSyncFailure(latestOp.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Advanced Filter Update Ops ──────────────────────────────

  /**
   * Processes ADVANCED_FILTER_UPDATE ops.
   * Groups by viewTypeId and takes the latest advanced filter state per view.
   * Calls /api/database/settings/filter with the advancedFilters body shape.
   */
  private async processAdvancedFilterUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const latestByView = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as AdvancedFilterUpdatePayload;
      const key = payload.viewTypeId;
      // Pick the latest op for this view. createdAt is millisecond-precision so
      // ties are possible when ops are queued in the same tick — fall back to
      // op.id (UUID) lexicographic order so the winner is deterministic.
      const current = latestByView.get(key);
      if (
        !current ||
        op.createdAt > current.createdAt ||
        (op.createdAt === current.createdAt && op.id > current.id)
      ) {
        latestByView.set(key, op);
      }
    }

    for (const [viewTypeId, latestOp] of latestByView.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as AdvancedFilterUpdatePayload).viewTypeId === viewTypeId)
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as AdvancedFilterUpdatePayload;
        await postWithAuth("/api/database/settings/filter", {
          blockId: payload.blockId,
          viewTypeId: payload.viewTypeId,
          advancedFilters: payload.advancedFilters,
        });

        await markOpsSynced(idsToMark);

        if (latestOp.noteId) {
          await markBlockSynced(latestOp.noteId);
        }

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync advanced filters for view ${viewTypeId}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        if (latestOp.noteId) {
          await recordBlockSyncFailure(latestOp.noteId, errorMsg);
        }
      }
    }
  }

  // ─── Page Settings Update Ops ────────────────────────────────

  /**
   * Processes PAGE_SETTINGS_UPDATE ops.
   * Groups by blockId (page) and takes the latest page settings per page.
   * Calls /api/blocks/update-page-settings.
   */
  private async processPageSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const latestByPage = new Map<string, SyncQueueRecord>();
    for (const op of ops) {
      const payload = op.payload as PageSettingsUpdatePayload;
      const key = payload.blockId;
      const current = latestByPage.get(key);
      if (
        !current ||
        op.createdAt > current.createdAt ||
        (op.createdAt === current.createdAt && op.id > current.id)
      ) {
        latestByPage.set(key, op);
      }
    }

    for (const [blockId, latestOp] of latestByPage.entries()) {
      const idsToMark = ops
        .filter((op) => (op.payload as PageSettingsUpdatePayload).blockId === blockId)
        .map((op) => op.id);
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(idsToMark);
        continue;
      }

      try {
        const payload = latestOp.payload as PageSettingsUpdatePayload;
        await postWithAuth("/api/blocks/update-page-settings", {
          blockId: payload.blockId,
          settings: payload.settings,
        });

        await markOpsSynced(idsToMark);

        await markBlockSynced(blockId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(`[SyncWatcher] Failed to sync page settings for page ${blockId}:`, errorMsg);

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        await recordBlockSyncFailure(blockId, errorMsg);
      }
    }
  }

  /**
   * Processes DATASOURCE_PAGE_SETTINGS_UPDATE ops.
   * Groups by dataSourceId, merges all pending settings for that datasource into
   * a single payload (latest write wins per key), and POSTs to /api/database/page-settings.
   */
  private async processDatasourcePageSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    // Process oldest first so newer ops overwrite older ones in the merged payload.
    const sorted = [...ops].sort(
      (a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );

    const mergedByDS = new Map<
      string,
      { blockId: string; settings: Record<string, any>; opIds: string[] }
    >();
    for (const op of sorted) {
      const payload = op.payload as DatasourcePageSettingsUpdatePayload;
      const entry = mergedByDS.get(payload.dataSourceId) ?? {
        blockId: payload.blockId,
        settings: {},
        opIds: [],
      };
      entry.blockId = payload.blockId || entry.blockId;
      entry.settings = { ...entry.settings, ...(payload.settings || {}) };
      entry.opIds.push(op.id);
      mergedByDS.set(payload.dataSourceId, entry);
    }

    for (const [dataSourceId, { blockId, settings, opIds }] of mergedByDS.entries()) {
      // Pick a representative op for retry/attempt accounting (latest one wins).
      const latestOp = sorted
        .filter((op) => (op.payload as DatasourcePageSettingsUpdatePayload).dataSourceId === dataSourceId)
        .pop()!;
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(opIds);
        continue;
      }

      try {
        await postWithAuth("/api/database/page-settings", {
          dataSourceId,
          blockId,
          settings,
        });

        await markOpsSynced(opIds);
        await markDataSourceSynced(dataSourceId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(
          `[SyncWatcher] Failed to sync datasource page settings for ${dataSourceId}:`,
          errorMsg,
        );

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        await recordDataSourceSyncFailure(dataSourceId, errorMsg);
      }
    }
  }

  /**
   * Processes DATASOURCE_SETTINGS_UPDATE ops.
   * Groups by dataSourceId, merges all pending settings for that datasource into
   * a single payload (latest write wins per key), and POSTs to /api/database/datasource/settings.
   */
  private async processDatasourceSettingsUpdateOps(ops: SyncQueueRecord[]): Promise<void> {
    const sorted = [...ops].sort(
      (a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );

    const mergedByDS = new Map<
      string,
      { blockId: string; settings: Record<string, any>; opIds: string[] }
    >();
    for (const op of sorted) {
      const payload = op.payload as DatasourceSettingsUpdatePayload;
      const entry = mergedByDS.get(payload.dataSourceId) ?? {
        blockId: payload.blockId,
        settings: {},
        opIds: [],
      };
      entry.blockId = payload.blockId || entry.blockId;
      entry.settings = { ...entry.settings, ...(payload.settings || {}) };
      entry.opIds.push(op.id);
      mergedByDS.set(payload.dataSourceId, entry);
    }

    for (const [dataSourceId, { blockId, settings, opIds }] of mergedByDS.entries()) {
      const latestOp = sorted
        .filter((op) => (op.payload as DatasourceSettingsUpdatePayload).dataSourceId === dataSourceId)
        .pop()!;
      if (latestOp.attempts >= MAX_ATTEMPTS) {
        await this.deadLetterOps(opIds);
        continue;
      }

      try {
        await postWithAuth("/api/database/datasource/settings", {
          dataSourceId,
          blockId,
          settings,
        });

        await markOpsSynced(opIds);
        await markDataSourceSynced(dataSourceId);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error(
          `[SyncWatcher] Failed to sync datasource settings for ${dataSourceId}:`,
          errorMsg,
        );

        await recordOpFailure(latestOp.id, errorMsg);
        retryScheduler.schedule(latestOp.id, latestOp.attempts + 1, () => this.wake());

        await recordDataSourceSyncFailure(dataSourceId, errorMsg);
      }
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────

  /**
   * Permanently fail one or more ops after MAX_ATTEMPTS.
   * Marks them as isSync: 2 (dead-letter) and cancels any pending retry timer
   * so getUnsyncedOps no longer returns them and the sync loop stops being woken.
   */
  private async deadLetterOps(opIds: string[]): Promise<void> {
    if (opIds.length === 0) return;
    await markOpsDeadLetter(opIds);
    for (const id of opIds) retryScheduler.cancel(id);
    console.warn(
      `[SyncWatcher] Dead-lettered ${opIds.length} op(s) after ${MAX_ATTEMPTS} failed attempts:`,
      opIds,
    );
  }

  /** Split an array into chunks of size n. */
  private chunk<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  /** Update the active workspace (called when user switches workspaces). */
  setWorkspace(workspaceId: string): void {
    this.workspaceId = workspaceId;
    // Drain anything already queued for this workspace — without this, its
    // pending ops wait for the next edit or tab hide/show.
    this.wake();
  }
}

// Singleton — import and call syncWatcher.initialize() once in providers.tsx
export const syncWatcher = new SyncWatcher();
